// Saved flights — pushed from the active plan into a persistent list.
// Each flight is a snapshot of route + ETD + cruise alt + aircraft +
// (when recorded) a track-log rollup for the FAA logbook view.

import { kvGet, kvSet } from './idb';
import { trackAuditEvent } from './audit';
import type { PlanPoint } from '../stores/planStore';
import {
  pushRow,
  pushMany,
  deleteRowByClientId,
  pullAll,
  type SyncBindings,
} from './syncCore';
import { useAuthStore } from '../stores/authStore';
import { activeOrgId } from '../stores/accountContextStore';

export type FlightStatus = 'planned' | 'active' | 'completed' | 'cancelled';

export type LogbookRole =
  | 'pic'         // Pilot in Command
  | 'sic'         // Second in Command
  | 'dual-given'  // Instructor giving instruction
  | 'dual-received' // Student under instruction
  | 'solo'        // Student solo
  | 'safety';     // Safety pilot under simulated instrument

/** Logbook-grade rollup computed from a recorded track or entered by
 *  the pilot. All fields optional so historical flights (saved before
 *  this schema existed) continue to load. */
export interface LogbookRollup {
  /** Total flight time in MINUTES (wheels-up to wheels-down). */
  totalMin?: number;
  /** FAA-style mountainous-terrain time in minutes. */
  mountainousMin?: number;
  /** Non-mountainous time in minutes (totalMin - mountainousMin). */
  nonMountainousMin?: number;
  /** Highest altitude reached in feet MSL. */
  maxAltFt?: number;
  /** Total ground-track distance in nautical miles. */
  distanceNm?: number;
  /** Number of GPS fixes the rollup was computed from. */
  sampleCount?: number;
  /** Source of the rollup. */
  source?: 'track-log' | 'manual';
  /** Operational mountainous classifier label used. */
  classifier?: 'agl3000-relief' | 'far95' | 'manual';
  /** When the rollup was last computed (epoch ms). */
  computedAt?: number;
}

export interface StationLoad {
  stationId: string;
  weightLb: number;
}

export interface FlightFuelOverride {
  /** Override for starting fuel in gallons (default = aircraft.fuel.totalUsable). */
  startingGal?: number;
  /** Override for taxi/start fuel in gallons (default = aircraft.fuel.startTaxiTakeoff). */
  taxiGal?: number;
}

export interface Flight {
  id: string;
  createdAt: number;
  updatedAt: number;
  /** Human label, e.g. "KSIY → KMFR" */
  title: string;
  status: FlightStatus;
  route: PlanPoint[];
  /** ISO timestamp */
  etd: string | null;
  /** Cruise altitude (ft MSL) */
  cruiseAlt: number | null;
  /** Aircraft profile id (in aircraftStore) */
  aircraftId: string | null;
  remarks: string;

  // ---- Logbook columns (all optional; pilot can fill at any time) ----
  /** Pilot in Command. References pilotStore. */
  picId?: string | null;
  /** Second in Command (if any). */
  sicId?: string | null;
  /** Role flown by the default user. Defaults to 'pic'. */
  role?: LogbookRole;
  /** Free-form tags: 'IFR', 'night', 'simulated-instrument',
   *  'fire-dispatch', 'medevac', 'mountain', etc. */
  tags?: string[];
  /** Optional link to a recorded track. References trackStore. */
  trackId?: string | null;
  /** Auto-derived (or manually entered) flight rollup. */
  rollup?: LogbookRollup;

  // ---- Flight-detail additions (all optional — old flights load fine) ----
  /** ICAO ident of the alternate airport. */
  alternateIdent?: string | null;
  /** Resolved alternate airport PlanPoint. */
  alternate?: PlanPoint | null;
  /** Selected performance profile id (in aircraft.performanceProfiles). */
  performanceProfileId?: string | null;
  /** Per-station W&B loads entered by the pilot. */
  stationLoads?: StationLoad[];
  /** Pilot-entered fuel overrides. */
  fuel?: FlightFuelOverride;

  /** When set, this flight is shared with the organization — every
   *  member can read AND edit unless `private` is true. The flight's
   *  owner_id stays the same; only the visibility broadens. */
  orgId?: string | null;
  /** When true (and orgId is set), this flight stays private to the
   *  owner even though it's tagged to the org. Lets a pilot opt out
   *  of sharing for a sensitive trip. Default false. */
  private?: boolean;

  /** Incident this flight was conducted for (fire response, contracted
   *  ops, etc.). Used by the Incident detail page's billing rollup to
   *  sum flight hours per aircraft per fire. Null = personal/training
   *  flight. Added 2026-05-12. */
  incidentId?: string | null;
}

const KV_KEY = 'flights.v1';

/** Sort a flights array newest-first by createdAt. Defensive — the IDB
 *  payload may have been written in any order (server-pulled rows have
 *  no inherent order; createFlight prepends to whatever was there).
 *  Boss report 2026-05-25 13:57 PT: flight list wasn't ordered by date. */
export function sortFlightsDesc(list: Flight[]): Flight[] {
  return [...list].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export async function loadFlights(): Promise<Flight[]> {
  const list = await kvGet<Flight[]>(KV_KEY);
  return Array.isArray(list) ? sortFlightsDesc(list) : [];
}

async function saveFlights(list: Flight[]): Promise<void> {
  await kvSet(KV_KEY, sortFlightsDesc(list));
}

export async function createFlight(input: Omit<Flight, 'id' | 'createdAt' | 'updatedAt' | 'status'> & { status?: FlightStatus }): Promise<Flight> {
  const now = Date.now();
  // If the user is currently in org context and didn't explicitly
  // pass an orgId, default to the active org so the flight is
  // automatically shared with the crew.
  const ctxOrgId = activeOrgId();
  const orgIdToUse = input.orgId !== undefined ? input.orgId : ctxOrgId;
  const flight: Flight = {
    id: 'flt-' + now.toString(36) + Math.random().toString(36).slice(2, 6),
    createdAt: now,
    updatedAt: now,
    status: input.status ?? 'planned',
    title: input.title,
    route: input.route,
    etd: input.etd,
    cruiseAlt: input.cruiseAlt,
    aircraftId: input.aircraftId,
    remarks: input.remarks,
    picId: input.picId,
    sicId: input.sicId,
    role: input.role,
    tags: input.tags,
    trackId: input.trackId,
    rollup: input.rollup,
    alternateIdent: input.alternateIdent,
    alternate: input.alternate,
    performanceProfileId: input.performanceProfileId,
    stationLoads: input.stationLoads,
    fuel: input.fuel,
    orgId: orgIdToUse,
    private: input.private ?? false,
  };
  // Serialize through the same queue used by realtime so a create
  // landing concurrently with a sync or a realtime INSERT can't be
  // overwritten by a stale snapshot.
  await realtimeQueue.run(async () => {
    const list = await loadFlights();
    await saveFlights([flight, ...list]);
  });
  void pushFlightRow(flight);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('fyrefly:flights:changed'));
  }
  return flight;
}

export async function updateFlight(id: string, patch: Partial<Flight>): Promise<Flight | null> {
  let updated: Flight | null = null;
  let prevForAudit: Flight | null = null;
  // Serialize through the queue so concurrent realtime / sync activity
  // can't trample the patch we're about to write.
  await realtimeQueue.run(async () => {
    const list = await loadFlights();
    const next = list.map((f) => {
      if (f.id !== id) return f;
      prevForAudit = f;
      updated = { ...f, ...patch, id: f.id, updatedAt: Date.now() };
      return updated;
    });
    await saveFlights(next);
  });
  if (updated) {
    void pushFlightRow(updated);
    // ── Audit hooks (fire-and-forget) ────────────────────────────────
    // Detect status transitions (planned → active → completed → cancelled)
    // and route/cruise edits. Other field-level edits don't generate
    // their own audit events — the underlying row already records
    // the latest state on every push.
    try {
      const prev = prevForAudit as Flight | null;
      const nextFlight = updated as Flight;
      if (prev && prev.status !== nextFlight.status) {
        trackAuditEvent({
          kind: 'flight.status_change',
          subjectKind: 'flight',
          subjectId: nextFlight.id,
          payload: {
            from: prev.status ?? null,
            to: nextFlight.status ?? null,
            flightTitle: nextFlight.title ?? null,
          },
        });
      }
      // Plan-modified covers route edits, cruise alt changes, and ETD
      // shifts. We diff by JSON.stringify on the route array — it's
      // small (typically <50 waypoints) and deterministic.
      if (prev) {
        const routeChanged =
          JSON.stringify(prev.route ?? []) !== JSON.stringify(nextFlight.route ?? []);
        const cruiseChanged = (prev.cruiseAlt ?? null) !== (nextFlight.cruiseAlt ?? null);
        const etdChanged = (prev.etd ?? null) !== (nextFlight.etd ?? null);
        if (routeChanged || cruiseChanged || etdChanged) {
          trackAuditEvent({
            kind: 'flight.plan_modified',
            subjectKind: 'flight',
            subjectId: nextFlight.id,
            payload: {
              flightTitle: nextFlight.title ?? null,
              routeChanged,
              cruiseChanged,
              etdChanged,
              routeIdents: routeChanged ? (nextFlight.route ?? []).map((p) => p.ident) : undefined,
              cruiseAltBefore: cruiseChanged ? (prev.cruiseAlt ?? null) : undefined,
              cruiseAltAfter:  cruiseChanged ? (nextFlight.cruiseAlt ?? null) : undefined,
              etdBefore: etdChanged ? (prev.etd ?? null) : undefined,
              etdAfter:  etdChanged ? (nextFlight.etd ?? null) : undefined,
            },
          });
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[flights] audit hook failed:', e);
    }
  }
  return updated;
}

export async function deleteFlight(id: string): Promise<void> {
  await realtimeQueue.run(async () => {
    const list = await loadFlights();
    await saveFlights(list.filter((f) => f.id !== id));
  });
  void deleteRowByClientId(id, FLIGHTS_SYNC);
}

export async function getFlight(id: string): Promise<Flight | null> {
  const list = await loadFlights();
  return list.find((f) => f.id === id) ?? null;
}

export function flightTitle(route: PlanPoint[]): string {
  if (route.length === 0) return 'Empty plan';
  if (route.length === 1) return route[0].ident;
  // Use the first and last AIRPORT rows for the title, not just the
  // first and last route entries. Without this, a route with a loaded
  // IAP gets titled e.g. "O46 -> WDSTC" because the missed-approach
  // legs are tacked on after the destination airport row by
  // spliceProcedureIntoRoute's IAP-split logic. Boss flagged the bug
  // 2026-05-17 when the Flights page showed "O46 -> missed-approach
  // hold fix" instead of "O46 -> KSTS".
  const firstAirport = route.find((p) => p.kind === 'airport');
  let lastAirport: PlanPoint | undefined;
  for (let i = route.length - 1; i >= 0; i--) {
    if (route[i].kind === 'airport') { lastAirport = route[i]; break; }
  }
  const start = firstAirport ?? route[0];
  const end = lastAirport ?? route[route.length - 1];
  if (start === end) return start.ident;
  return `${start.ident} \u2192 ${end.ident}`;
}

// ---- Sync layer -----------------------------------------------------

interface FlightDbRow {
  owner_id: string;
  org_id: string | null;
  private: boolean;
  client_id: string;
  title: string;
  status: string;
  etd: string | null;
  cruise_alt: number | null;
  remarks: string | null;
  aircraft_client_id: string | null;
  pic_client_id: string | null;
  sic_client_id: string | null;
  track_client_id: string | null;
  role: string | null;
  tags: unknown;
  route: unknown;
  rollup: unknown;
  alternate: unknown;
  alternate_ident: string | null;
  performance_profile_id: string | null;
  station_loads: unknown;
  fuel_override: unknown;
  /** Legacy uuid FK to incidents.id — nullable since 2026-05-13.
   *  New writes leave this null; reads tolerate it for any
   *  pre-migration rows. */
  incident_id: string | null;
  /** Denormalized client_id of the parent incident. Composite FK to
   *  (owner_id, incidents.client_id) on the server. The Flight type
   *  carries this as `incidentId` (local client_id of the incident). */
  incident_client_id: string | null;
  // Plus the column-managed timestamps PostgREST ignores in upserts.
  created_at?: string;
  updated_at?: string;
}

const FLIGHTS_SYNC: SyncBindings<Flight, FlightDbRow> = {
  tableName: 'flights',
  upsertConflict: 'owner_id,client_id',
  localIdField: 'id',
  toDbRow(f, ownerId) {
    return {
      owner_id: ownerId,
      org_id: f.orgId ?? null,
      private: f.private ?? false,
      client_id: f.id,
      title: f.title ?? '',
      status: f.status ?? 'planned',
      etd: f.etd ?? null,
      cruise_alt: f.cruiseAlt ?? null,
      remarks: f.remarks ?? null,
      aircraft_client_id: f.aircraftId ?? null,
      pic_client_id: f.picId ?? null,
      sic_client_id: f.sicId ?? null,
      track_client_id: f.trackId ?? null,
      role: f.role ?? null,
      tags: f.tags ?? [],
      route: f.route ?? [],
      rollup: f.rollup ?? null,
      alternate: f.alternate ?? null,
      alternate_ident: f.alternateIdent ?? null,
      performance_profile_id: f.performanceProfileId ?? null,
      station_loads: f.stationLoads ?? null,
      fuel_override: f.fuel ?? null,
      // f.incidentId is the local client_id of the incident, not a
      // server uuid. Send it via the denormalized text column and
      // leave the legacy uuid column null.
      incident_id: null,
      incident_client_id: f.incidentId ?? null,
    };
  },
  fromDbRow(row) {
    return {
      id: row.client_id,
      orgId: row.org_id ?? null,
      private: !!row.private,
      title: row.title ?? '',
      status: (row.status as FlightStatus) ?? 'planned',
      etd: row.etd ?? null,
      cruiseAlt: row.cruise_alt ?? null,
      aircraftId: row.aircraft_client_id ?? null,
      remarks: row.remarks ?? '',
      picId: row.pic_client_id ?? null,
      sicId: row.sic_client_id ?? null,
      trackId: row.track_client_id ?? null,
      role: (row.role as Flight['role']) ?? undefined,
      tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
      route: Array.isArray(row.route) ? (row.route as PlanPoint[]) : [],
      rollup: row.rollup ? (row.rollup as Flight['rollup']) : undefined,
      alternate: row.alternate ? (row.alternate as PlanPoint) : undefined,
      alternateIdent: row.alternate_ident ?? undefined,
      performanceProfileId: row.performance_profile_id ?? undefined,
      stationLoads: Array.isArray(row.station_loads)
        ? (row.station_loads as StationLoad[])
        : undefined,
      fuel: row.fuel_override ? (row.fuel_override as FlightFuelOverride) : undefined,
      // Prefer denormalized client-id join; fall back to legacy uuid
      // column for any pre-migration rows.
      incidentId: row.incident_client_id ?? row.incident_id ?? null,
      createdAt: row.created_at ? Date.parse(row.created_at) : Date.now(),
      updatedAt: row.updated_at ? Date.parse(row.updated_at) : Date.now(),
    };
  },
};

async function pushFlightRow(f: Flight): Promise<void> {
  await pushRow(f, FLIGHTS_SYNC);
}

/** Pull authoritative flights state from the server. Pushes any
 *  local-only rows up first so a fresh sign-in doesn't lose data. */
export async function syncFlightsFromServer(): Promise<void> {
  try {
    // Push first — abort before pull if push fails to avoid overwriting
    // local-only rows with server state that doesn't include them.
    const localAtStart = await loadFlights();
    if (localAtStart.length > 0) {
      const pushResult = await pushMany(localAtStart, FLIGHTS_SYNC);
      if (!pushResult.ok) {
        console.warn('[flights] syncFromServer push failed:', pushResult.error);
        return;
      }
    }
    const r = await pullAll(FLIGHTS_SYNC);
    if (r.ok) {
      // RE-READ + write under the serialization queue. Between the
      // pushMany above and now, createFlight() may have added new rows
      // to IDB that haven't pushed yet. Without the re-read (and the
      // queue making it atomic) we'd diff against a STALE snapshot and
      // silently drop those rows. Boss report 2026-05-25 13:57 PT:
      // 'I keep creating flights and they only intermittently get
      // stored there in the list view' — root cause was this race.
      await realtimeQueue.run(async () => {
        const localNow = await loadFlights();
        const serverIds = new Set(r.rows.map((f) => f.id));
        const localOnly = localNow.filter((f) => !serverIds.has(f.id));
        await saveFlights([...r.rows, ...localOnly]);
      });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('fyrefly:flights:changed'));
      }
    } else {
      console.warn('[flights] syncFromServer pull failed:', r.error);
    }
  } catch (e) {
    console.warn('[flights] syncFromServer threw:', e);
  }
}

// Subscribe to auth: trigger a sync whenever a session appears.
if (typeof window !== 'undefined') {
  useAuthStore.subscribe((state, prev) => {
    const wasIn = !!prev.user;
    const isIn = !!state.user;
    if (!wasIn && isIn) void syncFlightsFromServer();
  });
  // Also kick a sync if a session is already live at module load.
  // Wrapped in setTimeout to step over any auth init in progress.
  setTimeout(() => {
    if (useAuthStore.getState().session) void syncFlightsFromServer();
  }, 0);
}

// ---- Realtime: live shared-plan updates ----------------------------
//
// When a member of the same org updates a shared flight, every other
// member should see the change immediately. We listen on the
// supabase_realtime publication (enabled on app.flights in migration
// 0022) for INSERT / UPDATE / DELETE events; RLS-filtered to rows the
// caller can see.
//
// Pattern: keep one channel open per signed-in session. On each event,
// merge into local IDB + emit a window event so React stores can
// re-read. Cheap because the payload is tiny (one row).

import { supabase } from './supabase';

let realtimeChannel: ReturnType<NonNullable<typeof supabase>['channel']> | null = null;

async function applyFlightChange(
  kind: 'INSERT' | 'UPDATE' | 'DELETE',
  oldRow: Partial<FlightDbRow> | null,
  newRow: Partial<FlightDbRow> | null,
): Promise<void> {
  // Serialize realtime-driven mutations through a per-tab queue so a
  // burst of postgres_changes events can't trample each other. Each
  // handler RE-READS local at the moment of mutation rather than at
  // the start of the async chain. Without this, two concurrent INSERTs
  // both read stale local and the second clobbers the first.
  return realtimeQueue.run(async () => {
    if (kind === 'DELETE') {
      const cid = oldRow?.client_id;
      if (!cid) return;
      const local = await loadFlights();
      const next = local.filter((f) => f.id !== cid);
      if (next.length !== local.length) {
        await saveFlights(next);
        window.dispatchEvent(new CustomEvent('fyrefly:flights:changed'));
      }
      return;
    }
    if (!newRow || !newRow.client_id) return;
    const updated = FLIGHTS_SYNC.fromDbRow(newRow as FlightDbRow);
    const local = await loadFlights();
    const existingIdx = local.findIndex((f) => f.id === updated.id);
    let next: Flight[];
    if (existingIdx >= 0) {
      next = [...local];
      next[existingIdx] = updated;
    } else {
      next = [updated, ...local];
    }
    await saveFlights(next);
    window.dispatchEvent(new CustomEvent('fyrefly:flights:changed'));
  });
}

// Tiny single-writer queue. Pushes mutations through a chain of
// promises so concurrent realtime events / syncs / creates can't
// race the read-modify-write against IDB.
const realtimeQueue = {
  chain: Promise.resolve() as Promise<unknown>,
  run<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.chain.then(fn, fn);
    // Swallow rejections on the shared chain so one failed mutation
    // doesn't poison every subsequent queued operation.
    this.chain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  },
};

function stopRealtime(): void {
  if (realtimeChannel && supabase) {
    try { supabase.removeChannel(realtimeChannel); } catch { /* ignore */ }
  }
  realtimeChannel = null;
}

function startRealtime(): void {
  if (!supabase) return;
  if (realtimeChannel) return; // already running
  // Channel listens on the `app.flights` table. RLS on the server
  // filters which rows the client gets to see, so we don't need a
  // client-side filter here — the server only sends shared rows for
  // orgs the user is in.
  realtimeChannel = supabase
    .channel('fyrefly:flights')
    .on(
      'postgres_changes',
      // Use schema='app' so the realtime server scopes to our table.
      { event: '*', schema: 'app', table: 'flights' },
      (payload) => {
        const kind = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
        const oldRow = (payload.old as Partial<FlightDbRow>) || null;
        const newRow = (payload.new as Partial<FlightDbRow>) || null;
        void applyFlightChange(kind, oldRow, newRow);
      },
    )
    .subscribe();
}

if (typeof window !== 'undefined') {
  useAuthStore.subscribe((state, prev) => {
    if (!prev.user && state.user) startRealtime();
    if (prev.user && !state.user) stopRealtime();
  });
  setTimeout(() => {
    if (useAuthStore.getState().user) startRealtime();
  }, 0);
}
